<template>
  <Dialog :common-dialog="dialog" :dialog-width="dialogWidth">
    <template v-slot:title>
      <div class="d-flex align-center justify-space-between w-100 px-2 py-1">
        <div class="d-flex align-center">
          <v-avatar size="38" color="cyan lighten-5" class="mr-3">
            <v-icon color="cyan darken-2"
              >mdi-account-box-multiple-outline</v-icon
            >
          </v-avatar>
          <div class="dialog-title color-custom-blue font-weight-700">
            Select Customer
          </div>
        </div>
        <v-btn
          icon
          small
          class="custom-grey-border bg-slate-50"
          :disabled="loading"
          v-on:click="$emit('close', true)"
        >
          <v-icon color="slate-600" small>mdi-close</v-icon>
        </v-btn>
      </div>
    </template>
    <template v-slot:body>
      <div class="px-4 w-100">
        <!-- SEARCH & ACTION TOOLBAR -->
        <v-card flat outlined class="rounded-lg bg-slate-50 px-3 py-2 mb-3">
          <label
            for="customer-search"
            class="font-size-13 font-weight-600 mb-1 d-block text-slate-700"
          >
            Which customer would you like to select this for?
          </label>
          <v-row dense align="center">
            <v-col cols="12" sm="6" md="6">
              <v-text-field
                id="customer-search"
                ref="customerSearch"
                v-model="search"
                placeholder="Search Customers..."
                dense
                filled
                solo
                flat
                hide-details
                color="cyan"
                prepend-inner-icon="mdi-magnify"
                :loading="loading"
                :disabled="loading"
                class="custom-erp-input bg-white rounded-lg"
                @keydown.enter.prevent="getCustomers(true)"
              />
            </v-col>
            <v-col cols="12" sm="6" md="6" class="d-flex align-center gap-2">
              <v-btn
                v-on:click="getCustomers()"
                :loading="loading"
                :disabled="loading"
                depressed
                color="cyan darken-2 white--text"
                class="custom-bold-button mr-2"
                height="36"
              >
                <v-icon small left>mdi-magnify</v-icon>
                Search
              </v-btn>

              <v-btn
                v-on:click="createCustomer()"
                :loading="loading"
                :disabled="loading"
                depressed
                color="custom-btn-color white--text"
                class="custom-bold-button"
                height="36"
              >
                <v-icon small left>mdi-plus</v-icon>
                Create Customer
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <!-- CUSTOMER LIST AREA -->
        <perfect-scrollbar
          :options="{ suppressScrollX: true }"
          class="scroll pr-1"
          style="max-height: calc(100vh - 340px); position: relative"
        >
          <div v-if="list.length">
            <v-card
              v-for="(row, index) in list"
              :key="`customer-list-key-${index}`"
              flat
              outlined
              class="rounded-lg px-3 py-2 mb-2 bg-white hover-shadow cursor-pointer transition-fast"
              v-on:click="selectCustomer(row)"
            >
              <v-row dense align="center">
                <!-- Left: Customer Details -->
                <v-col cols="12" md="8" class="py-1">
                  <div class="d-flex align-center flex-wrap gap-2 mb-1">
                    <Barcode :barcode="row.barcode" />
                    <v-chip
                      color="blue white--text"
                      label
                      small
                      class="ml-1"
                      style="height: 22px"
                      v-if="row.company_type == 'company'"
                    >
                      <span class="font-size-12 font-weight-500">
                        Company
                      </span>
                    </v-chip>
                    <v-chip
                      color="red white--text"
                      label
                      small
                      class="ml-1"
                      style="height: 22px"
                      v-if="row.company_type == 'personal'"
                    >
                      <span class="font-size-12 font-weight-500">
                        Individual
                      </span>
                    </v-chip>
                  </div>

                  <div
                    class="font-weight-700 font-size-14 color-custom-blue mb-1 d-flex align-center custom-nowrap-ellipsis"
                  >
                    <v-icon small left color="slate-600">mdi-domain</v-icon>
                    <template v-if="row.company_name">
                      {{ row.company_name }}
                    </template>
                    <em v-else class="text-muted font-italic font-weight-400"
                      >no company name</em
                    >
                  </div>

                  <div
                    class="d-flex align-center flex-wrap font-size-12 text-slate-700 gap-3"
                  >
                    <span
                      v-if="row?.default_person?.display_name"
                      class="d-flex align-center mr-3"
                    >
                      <v-icon small left color="slate-500"
                        >mdi-account-circle-outline</v-icon
                      >
                      <span class="font-weight-600 text-muted mr-1"
                        >Contact:</span
                      >
                      <span class="font-weight-500 text-slate-900">{{
                        row?.default_person?.display_name
                      }}</span>
                    </span>

                    <span
                      v-if="
                        row?.single_billing?.street_1 ||
                        row?.single_billing?.street_2
                      "
                      class="d-flex align-center font-weight-500 custom-nowrap-ellipsis"
                    >
                      <v-icon small left color="slate-500"
                        >mdi-map-marker-outline</v-icon
                      >
                      {{ row?.single_billing?.street_1 }}
                      {{ row?.single_billing?.unit_no }}
                      {{ row?.single_billing?.street_2 }}
                      <template
                        v-if="
                          row?.single_billing?.zip_code &&
                          row.single_billing.zip_code != '000000'
                        "
                      >
                        {{ row?.single_billing?.zip_code }}
                      </template>
                    </span>
                  </div>
                </v-col>

                <!-- Right: Select CTA & Counts -->
                <v-col
                  cols="12"
                  md="4"
                  class="text-md-right d-flex flex-column align-md-end justify-center py-1"
                >
                  <div class="d-flex align-center gap-2 mb-1">
                    <v-chip
                      x-small
                      color="slate-100"
                      class="font-weight-600 text-slate-700"
                    >
                      {{ row.property_count }} Site
                    </v-chip>
                    <v-chip
                      x-small
                      color="slate-100"
                      class="font-weight-600 text-slate-700 ml-1"
                    >
                      {{ row.persons_count }} Person
                    </v-chip>
                  </div>

                  <v-btn
                    small
                    depressed
                    color="cyan lighten-5"
                    class="custom-bold-button px-3 cyan--text text-darken-2"
                  >
                    Select Customer
                    <v-icon small right>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </div>

          <div
            v-else-if="loading"
            class="d-flex align-center justify-center py-10 px-4 border rounded-lg bg-slate-50"
          >
            <v-progress-linear
              indeterminate
              color="cyan darken-2"
              rounded
            />
          </div>

          <!-- EMPTY STATE -->
          <div
            v-else
            class="d-flex flex-column align-center justify-center py-10 px-4 text-center border rounded-lg bg-slate-50 border-dashed"
          >
            <v-avatar size="56" color="grey lighten-4" class="mb-3">
              <v-icon color="slate-400" large>mdi-account-off-outline</v-icon>
            </v-avatar>
            <div class="font-weight-700 font-size-15 text-slate-800 mb-1">
              No Customers Found
            </div>
            <div class="caption-1 text-muted max-w-300 mb-3">
              There are no matching customer records in the database.
            </div>
            <v-btn
              depressed
              color="custom-btn-color white--text"
              class="custom-bold-button"
              height="32"
              v-on:click="createCustomer()"
            >
              <v-icon small left>mdi-plus</v-icon>
              Create Customer
            </v-btn>
          </div>
        </perfect-scrollbar>
      </div>
      <customer-create
        v-if="showCustomerCreateDialog"
        :key="`enquiry-customer-create-${dialog_key}`"
      />
    </template>
  </Dialog>
</template>

<script>
import Dialog from "@/view/pages/partials/Dialog.vue";
import Barcode from "@/view/pages/partials/Barcode.vue";
import { QUERY } from "@/core/services/store/request.module";
import { SET_CUSTOMER } from "@/core/services/store/visit.module";
import ObjectPath from "object-path";
import CustomerCreate from "@/view/components/CustomerCreateDialog.vue";

import {
  SET_CREATE_DIALOG_STATUS,
  SET_DIALOG_STATUS,
  CLEAR_CUSTOMER,
} from "@/core/services/store/customer.module";

export default {
  name: "select-customer",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    showCustomerCreateDialog: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      dialog_key: 0,
      loading: false,
      search: null,
      list: [],
    };
  },
  watch: {
    dialog(param) {
      if (param) {
        console.log({ param });
        this.search = null;
        this.getCustomers();
      }
    },
  },
  components: {
    Dialog,
    Barcode,
    "customer-create": CustomerCreate,
  },
  methods: {
    focusSearchInput() {
      const searchField = this.$refs.customerSearch;
      if (searchField && typeof searchField.focus === "function") {
        searchField.focus();
        return;
      }

      const searchInput = searchField?.$el?.querySelector("input");
      if (searchInput) {
        searchInput.focus();
      }
    },
    selectCustomer(row) {
      if (this.loading) {
        return false;
      }
      this.loading = true;
      this.$store
        .dispatch(SET_CUSTOMER, row)
        .then(() => {
          this.$emit("close", true);
          this.search = null;
          this.list = [];
        })
        .catch((error) => {
          console.log({ error });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getCustomers(restoreFocus = false) {
      this.loading = true;
      this.list = [];
      this.$store
        .dispatch(QUERY, {
          url: "customer-list",
          data: { search: this.search },
        })
        .then((response) => {
          this.list = ObjectPath.get(response, "data", []);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
          if (restoreFocus) {
            this.$nextTick(() => {
              this.focusSearchInput();
            });
          }
        });
    },
    createCustomer() {
      if (!this.showCustomerCreateDialog) {
        this.$emit("create");
        return;
      }

      this.dialog_key = Number(new Date());
      this.$nextTick(() => {
        this.$store.commit(SET_CREATE_DIALOG_STATUS, true);
      });
    },
    beforeDestroy() {
      this.$store.dispatch(CLEAR_CUSTOMER);
    },
    beforeMount() {
      this.$store.dispatch(CLEAR_CUSTOMER);
    },
    beforeCreate() {
      this.$store.dispatch(CLEAR_CUSTOMER);
    },
    closeCustomerDialog() {
      this.$store.commit(SET_DIALOG_STATUS, false);
    },
  },
  computed: {
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 50);
    },
  },
};
</script>
